import { Avatar, Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { IconTeams } from '../../../icons/IconTeams'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { NotificationTypeChip } from './NotificationTypeChip'
import { mediaSourceUrl } from './utils'
import { createNotificationNavHandlers, getActorPathKey, getProjectSlug, getTeamSlug } from './notificationNav'
import { ActorNameSpan, ProjectNameQuoted } from './notificationTypography'
import { notificationBodyWrapSx } from './notificationTextClamp'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'project.team.removed' }>
}

export function ProjectTeamRemovedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onTeamClick, onUserClick, onProjectClick } = createNotificationNavHandlers(navigate)
  const payload = notification.payload ?? {}
  const project = payload.project ?? {}
  const team = payload.team ?? {}
  const actor = payload.actor ?? {}

  const teamName = typeof team.name === 'string' && team.name.trim().length > 0 ? team.name : 'команда'
  const projectTitle =
    typeof project.title === 'string' && project.title.trim().length > 0 ? project.title : 'проект'
  const actorName = typeof actor.fullname === 'string' && actor.fullname.trim().length > 0 ? actor.fullname : 'Пользователь'
  const message = typeof payload.message === 'string' && payload.message.trim().length > 0 ? payload.message.trim() : null
  const teamAvatarSrc = mediaSourceUrl(team.image_url) ?? undefined
  const teamSlug = getTeamSlug(team)
  const projectSlug = getProjectSlug(project)
  const actorPathKey = getActorPathKey(actor, notification.actor_user_id)
  const hasActor = Boolean(payload?.actor)

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onTeamClick(e, teamSlug)}
          sx={{ cursor: teamSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={teamName}
            avatarSrc={teamAvatarSrc}
            fallbackIcon={<IconTeams fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle={
              <>
                <Typography variant="black-12-light" component="span" sx={{ minWidth: 0, ...notificationBodyWrapSx }}>
                  Исключение из проекта
                </Typography>
                <NotificationTypeChip variant="team_removed" />
              </>
            }
          />
        </Box>

        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          {hasActor && (
            <Box
              onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
              sx={{ cursor: actorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
            >
              <Avatar src={mediaSourceUrl(actor.avatar_url) ?? undefined} alt={actorName} sx={{ width: 34, height: 34 }} />
            </Box>
          )}

          <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
            {hasActor ? (
              <>
                <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
                  {actorName}
                </ActorNameSpan>
                {' исключил(а) вашу команду из '}
                <ProjectNameQuoted name={projectTitle} pathKey={projectSlug} onNameClick={onProjectClick} />
              </>
            ) : (
              <>
                Вашу команду исключили из <ProjectNameQuoted name={projectTitle} pathKey={projectSlug} onNameClick={onProjectClick} />
              </>
            )}
          </Typography>
        </Stack>

        {message && (
          <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
            {message}
          </Typography>
        )}

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
